// Marketing-pixel entry shim — the ONLY part of the tracker in the initial bundle.
//
// The storefront ships under a hard gz entry budget (CLAUDE.md §4), so this file stays a
// queue plus a lazy hand-off: the first tracked event pulls ./runtime (and from
// there the per-platform adapters) in its own chunk, at idle, after first paint.
// Everything a call site touches is this `track()` and `initTracking()`.

import type { Router } from "vue-router"

import type { Address } from "../api"
import { isPreview } from "../previewFlag"
import { hostFragment } from "../visitor"
import type { TrackInput } from "./types"

type Sink = (e: TrackInput) => void

const queue: TrackInput[] = []
let sink: Sink | null = null
let booting = false

// Consent gate. It starts CLOSED and only `openTrackingGate()` opens it — App.vue
// calls that once bootstrap says either "no cookie banner configured" or "this
// visitor already accepted". Closed means events accumulate here and no adapter
// (and therefore no vendor script) is ever loaded, which is the whole point: a
// banner that lets the pixels load before the visitor answers is decoration.
//
// Defaulting closed also covers the race where the first page_view fires before
// bootstrap has resolved — holding a few events costs nothing, leaking them can't
// be undone.
let gateOpen = false
const MAX_HELD = 50

// Callbacks deferred until the gate opens, for call sites that bypass track()'s
// Norm pipeline entirely — klaviyoIdentify (klaviyo.ts) needs a plaintext email,
// which Norm deliberately never carries (every other adapter gets a SHA-256
// hash), so it cannot queue through `queue` above. This is the same
// queue-until-consent guarantee track() gives every Norm event, extended to the
// one call site that structurally can't go through track(). Not exported: the
// only consumer is identifyKlaviyo below, in this same file.
const gateCallbacks: Array<() => void> = []

function trackingAllowed(): boolean {
  return gateOpen && !isPreview()
}

function onTrackingGateOpen(cb: () => void): void {
  if (trackingAllowed()) {
    cb()
    return
  }
  gateCallbacks.push(cb)
}

/**
 * Identifies the current visitor to Klaviyo by email — a real address, never a
 * hash (klaviyo.ts's own doc comment has the reason: Norm never carries one).
 * This is the entry-resident half: it only gates on consent and dynamically
 * imports klaviyo.ts to do the actual work, so — like every vendor adapter —
 * that module stays out of the initial bundle. Call it from wherever Dikafy
 * already has a SERVER-VERIFIED email (customer login, the checkout contact
 * step), never from an unvalidated form field.
 */
export function identifyKlaviyo(email: string): void {
  onTrackingGateOpen(() => {
    void import("./klaviyo")
      .then(m => m.klaviyoIdentify(email))
      .catch(() => {
        /* tracking must never break the storefront */
      })
  })
}

/** A shopper Dikafy can name: an email, plus whatever of an address came with it. */
export type Buyer = Address & { email: string }

type BuyerListener = (b: Buyer | null) => void
let buyer: Buyer | null = null
const buyerListeners: BuyerListener[] = []
// Bumped by every forgetBuyer(), so identity work that started before a sign-out
// can tell it must not write afterwards (metamatch.remember).
let generation = 0

// Meta lets a pixel learn who the shopper is ONCE per page load (see meta.ts). When
// the adapter learns it now carries somebody else — a sign-out, or a different
// person naming themselves on a browser remembered as another — the only reset is
// a new page load, so the next navigation becomes one (initTracking below).
let identityStale = false
let identityWork: Promise<unknown> | null = null

// Every caller sits on a path that must not fail for tracking's sake — a login, a
// session restore whose catch signs the customer OUT, a checkout watcher.
function tell(l: BuyerListener, b: Buyer | null): void {
  try {
    l(b)
  } catch {
    /* tracking must never break the storefront */
  }
}

/**
 * Tells the ad pixels who this shopper is. Call it with what the shopper
 * SUBMITTED — a checkout contact the server accepted, a login, a registration,
 * a newsletter sign-up — never with a field still being typed.
 *
 * Plaintext, like identifyKlaviyo, and for the same reason it bypasses track():
 * Norm never carries a plaintext email, and the first-party beacon must never
 * see one. It is held in memory here and nowhere else until an adapter
 * subscribes via onBuyer(), and adapters exist only once the consent gate has
 * opened — so this is safe to call before the visitor has answered the banner.
 *
 * Synchronous on purpose: the Meta adapter re-inits its pixel inside this call,
 * so a track() on the very next line already carries the identity.
 */
export function identifyBuyer(b: Buyer): void {
  if (!b.email) return
  buyer = b
  for (const l of buyerListeners) tell(l, b)
}

/** Where the Meta adapter remembers a buyer's HASHES between visits. Named here
 *  rather than in the adapter so that signing out can clear it without loading
 *  the adapter — including on a visit whose consent gate never opens. */
export function matchStoreKey(): string {
  return `dk_match_${hostFragment()}`
}

/** Sign-out, or a refused cookie banner: this browser no longer belongs to that
 *  person, and nothing learned about them may outlive the decision. */
export function forgetBuyer(): void {
  buyer = null
  generation++
  try {
    localStorage.removeItem(matchStoreKey())
  } catch {
    /* storage blocked — then nothing was remembered either */
  }
  for (const l of buyerListeners) tell(l, null)
}

/** For adapters: called at once with the current buyer, if any, then on every change. */
export function onBuyer(l: BuyerListener): void {
  buyerListeners.push(l)
  if (buyer) tell(l, buyer)
}

/** How many sign-outs this page load has seen. */
export function buyerGeneration(): number {
  return generation
}

/** For adapters: the pixel now carries someone other than the current shopper. */
export function markIdentityStale(): void {
  identityStale = true
}

/** For adapters: identity work still settling (hashing, the stored copy). The
 *  navigation guard waits for it, so a reload never boots on the old identity. */
export function trackIdentityWork(p: Promise<unknown>): void {
  const w: Promise<unknown> = p
    .catch(() => {})
    .finally(() => {
      if (identityWork === w) identityWork = null
    })
  identityWork = w
}

/** Report an event. Safe to call before (or without) any pixel being configured. */
export function track(input: TrackInput): void {
  // The builder renders the storefront in an iframe; firing a seller's real pixels
  // from the editor would poison their conversion data.
  if (isPreview()) return
  if (sink) {
    sink(input)
    return
  }
  // Bounded: a visitor who declines browses on with the gate shut forever, and an
  // unbounded queue would just be a slow leak.
  if (queue.length < MAX_HELD) queue.push(input)
  if (!gateOpen || booting) return
  booting = true
  const start = () => {
    void import("./runtime")
      .then(m => m.boot())
      .catch(() => {
        /* tracking must never break the storefront */
      })
  }
  if ("requestIdleCallback" in window) window.requestIdleCallback(start)
  else setTimeout(start, 0)
}

/** Allow the pixels to load. Called by App.vue when no cookie banner is configured
 *  or when the visitor has accepted; whatever queued meanwhile is then sent. */
export function openTrackingGate(): void {
  if (gateOpen || isPreview()) return
  gateOpen = true
  if (queue.length && !booting) {
    // Re-enter through track() so the boot path stays in exactly one place.
    const held = queue.splice(0)
    for (const e of held) track(e)
  }
  if (gateCallbacks.length) {
    for (const cb of gateCallbacks.splice(0)) cb()
  }
}

/** runtime.boot() calls this to take over the channel and drain what queued. */
export function installSink(fn: Sink): void {
  sink = fn
  for (const e of queue.splice(0)) fn(e)
}

/** Page views for a CSR SPA: the first navigation plus every route change after. */
export function initTracking(router: Router): void {
  if (isPreview()) return
  // A stale pixel identity (markIdentityStale) turns the next navigation into a
  // full page load — fbevents starts fresh and the new boot reads who this browser
  // is NOW. Never INTO /upsell: the post-purchase funnel's session lives only in
  // memory (stores/checkout.ts) and a reload would lose it; the funnel ends on
  // /order/:token, which rebuilds from the server and is where the reload lands.
  router.beforeEach(async (to, from) => {
    if (!from.matched.length || to.path === "/upsell") return
    if (identityWork) await Promise.race([identityWork, new Promise(r => setTimeout(r, 500))])
    if (!identityStale) return
    window.location.assign(router.resolve(to).href)
    return false
  })
  void router.isReady().then(() => {
    track({ name: "page_view", path: router.currentRoute.value.fullPath })
    // Registered only after the initial navigation resolved, so it can't double-fire.
    router.afterEach(to => track({ name: "page_view", path: to.fullPath }))
  })
}
